import * as React from 'react';

import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { Button as ShadcnButton } from '@/hen-ui/shadcn/button';
import { useClampTriggerContext } from '@/hen-ui/hooks/clampTriggerContext';
import {
  BUTTON_ICON_SIZE_CLASS,
  BUTTON_ICON_STROKE_CLASS,
  BUTTON_PRESSED_FILL_VAR,
  buttonVariants,
  nativeButtonHeightClass,
  type ButtonIconColor,
  type ButtonIconSize,
  type ButtonVariantProps,
} from './button.variants';
import { Icon, type IconName } from '@/hen-ui/atomics/Icon';
import { Spinner } from '@/hen-ui/atomics/Spinner';
import {
  cardLinkHrefsMatch,
  useCardLinkContext,
} from '@/hen-ui/hooks/cardLinkContext';
import { cn } from '@/hen-ui/utils';

type NativeButtonProps = Omit<
  React.ComponentPropsWithoutRef<typeof ShadcnButton>,
  'variant' | 'size' | 'href' | 'target' | 'rel'
>;

type ButtonBaseProps = NativeButtonProps &
  ButtonVariantProps & {
    /**
     * Style. Role names: `primary`, `secondary` (Secondary Inverse with
     * `inverse`), `outlined` and `text`.
     *
     * Deprecated aliases that render identically: `primaryFill` (use
     * `primary`), `blackFill` (use `secondary`), `blackOutline` (use
     * `outlined`) and `textOnlyNeutral` (use `text`).
     */
    variant?: ButtonVariantProps['variant'];
    /**
     * Icon to display before the button text.
     * Lucide kebab name, or a host-injected `custom/{slug}` when IconRegistry is present.
     * @example
     * <Button iconBefore="plus">Add Item</Button>
     */
    iconBefore?: IconName;
    /**
     * Icon to display after the button text.
     * Lucide kebab name, or a host-injected `custom/{slug}` when IconRegistry is present.
     * @example
     * <Button iconAfter="arrow-right">Continue</Button>
     */
    iconAfter?: IconName;
    /** Icon to display after the text while the button is expanded. */
    expandedIconAfter?: IconName;
    /** Theme color for the button's icons. On the icon-only chip the saved fill matches it. */
    iconColor?: ButtonIconColor;
    /** Size of the button's icons. Unset follows the button size. */
    iconSize?: ButtonIconSize;
    /**
     * Pending submit. Shows a spinner, sets `aria-busy`, and ignores
     * activation until it clears. A bound flag may arrive as `"true"`.
     * While pending, a visually hidden status region announces `busyLabel`.
     *
     * With `asChild`, the spinner is not inserted and the child element's
     * own click handler still runs. The busy guard still blocks this
     * button's handler, and a busy link does not navigate.
     */
    busy?: boolean | 'true' | 'false';
    /**
     * Screen-reader text announced while `busy` is on. Defaults to
     * "Loading…".
     */
    busyLabel?: string;
  };

type AnchorButtonProps = ButtonBaseProps & {
  href: string;
  target?: React.AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  rel?: React.AnchorHTMLAttributes<HTMLAnchorElement>['rel'];
};

type RegularButtonProps = ButtonBaseProps & {
  href?: undefined;
  target?: never;
  rel?: never;
};

export type ButtonProps = AnchorButtonProps | RegularButtonProps;

/**
 * Button component following HEN Design System standards.
 *
 * A thin wrapper around shadcn/ui button with HEN-specific variants and design tokens.
 * All styling uses tokens from tailwind.config.ts.
 *
 * Inside a link card (`Card` with `href`), a Button to the card's destination,
 * or one with no `href`, `onClick`, or `type="submit"`, renders as a
 * visual-only `<span>`, and a forwarded `ref` receives that span.
 *
 * @example
 * // Basic usage
 * <Button>Click me</Button>
 *
 * @example
 * // With HEN variant and size
 * <Button variant="primaryOutline" size="lg">
 *   Large outlined button
 * </Button>
 *
 * @example
 * // As a link using asChild
 * <Button asChild variant="secondary">
 *   <a href="/about">About</a>
 * </Button>
 *
 * @example
 * // With icons (import from lucide-react)
 * import { Plus } from 'lucide-react';
 * <Button variant="primary">
 *   <Plus /> Create New
 * </Button>
 *
 * @example
 * // With iconBefore prop
 * <Button variant="primary" iconBefore="plus">
 *   Add Item
 * </Button>
 *
 * @example
 * // With iconAfter prop
 * <Button variant="primaryOutline" iconAfter="arrow-right">
 *   Continue
 * </Button>
 *
 * @example
 * // Stretch across the container, or stay as wide as the label
 * <Button width="full">Continue</Button>
 * <Button width="auto">See all</Button>
 *
 * @example
 * // Full width on a phone, only as wide as the label from a tablet up
 * <Button width="full" width-md="auto">Continue</Button>
 *
 * @example
 * // Text that stays underlined at rest
 * <Button variant="text" underline="always">
 *   Sign in
 * </Button>
 *
 * @example
 * // Brand gradient on hover (filled buttons)
 * <Button variant="primary" hoverTreatment="gradient">
 *   Check availability
 * </Button>
 */
export const Button = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    {
      className,
      variant,
      size,
      underline,
      hoverTreatment,
      inverse,
      width,
      'width-md': widthMd,
      'width-lg': widthLg,
      iconBefore,
      iconAfter,
      expandedIconAfter,
      iconColor,
      iconSize,
      busy,
      busyLabel,
      children,
      asChild,
      href,
      onClick,
      ...props
    },
    ref,
  ) => {
    const isBusy = busy === true || busy === 'true';
    const resolvedBusyLabel =
      typeof busyLabel === 'string' && busyLabel.trim().length > 0
        ? busyLabel.trim()
        : 'Loading…';
    // A bound flag is a string. Keep the status region mounted so the
    // announcement is a text change, not a node that appears already filled.
    const showStatusRegion = busy === true || typeof busy === 'string';
    const busyStatus = showStatusRegion ? (
      <span className='sr-only' role='status'>
        {isBusy ? resolvedBusyLabel : ''}
      </span>
    ) : null;
    const classNames = cn(
      buttonVariants({
        variant,
        size,
        underline,
        hoverTreatment,
        inverse,
        width,
        'width-md': widthMd,
        'width-lg': widthLg,
      }),
      className,
    );
    const clampTrigger = useClampTriggerContext();
    const cardLink = useCardLinkContext();
    // Inside a link card, a CTA that goes to the card's destination — or has
    // no action of its own — is visual only, so the card stays one tab stop.
    const demoteToPresentation =
      cardLink != null &&
      !clampTrigger &&
      !asChild &&
      (href
        ? cardLinkHrefsMatch(cardLink.href, href)
        : !onClick && props.type !== 'submit');
    const expanded = clampTrigger?.expanded ?? props['aria-expanded'];
    const isExpanded = expanded === true || expanded === 'true';
    const resolvedIconAfter =
      isExpanded && expandedIconAfter ? expandedIconAfter : iconAfter;
    const label = clampTrigger?.label ?? children;
    const iconClassName = cn(
      BUTTON_ICON_STROKE_CLASS,
      iconSize ? BUTTON_ICON_SIZE_CLASS[iconSize] : undefined,
    );
    const iconStyle = iconColor
      ? ({ [BUTTON_PRESSED_FILL_VAR]: 'currentColor' } as React.CSSProperties)
      : undefined;
    const content = (
      <>
        {isBusy ? (
          <Spinner
            aria-hidden={true}
            role='presentation'
            className='text-current'
          />
        ) : (
          iconBefore && (
            <Icon
              icon={iconBefore}
              aria-hidden={true}
              color={iconColor}
              className={iconClassName}
              style={iconStyle}
            />
          )
        )}
        {/* A text label needs its own box to keep its size while pressed. */}
        {typeof label === 'string' || typeof label === 'number' ? (
          <span>{label}</span>
        ) : (
          label
        )}
        {resolvedIconAfter && (
          <Icon
            icon={resolvedIconAfter}
            aria-hidden={true}
            color={iconColor}
            className={iconClassName}
            style={iconStyle}
          />
        )}
      </>
    );

    if (demoteToPresentation) {
      return (
        <span
          ref={ref as React.Ref<HTMLSpanElement>}
          aria-hidden='true'
          className={classNames}
        >
          {content}
        </span>
      );
    }

    if (href && !clampTrigger) {
      // When href is present the component renders as an <a>. We cast props to
      // anchor attributes so all forwarded props (onClick, target, rel, aria-*,
      // data-*, etc.) reach the element — nothing is silently dropped.
      const anchorProps =
        props as unknown as React.AnchorHTMLAttributes<HTMLAnchorElement>;
      return (
        <>
          <a
            ref={ref as React.Ref<HTMLAnchorElement>}
            data-hen-id={COMPONENT_IDS.Button}
            {...anchorProps}
            href={href}
            aria-busy={isBusy ? true : anchorProps['aria-busy']}
            aria-disabled={isBusy ? true : anchorProps['aria-disabled']}
            onClick={(event) => {
              if (isBusy) {
                event.preventDefault();
                return;
              }
              (
                onClick as unknown as
                  | React.MouseEventHandler<HTMLAnchorElement>
                  | undefined
              )?.(event);
            }}
            className={classNames}
          >
            {content}
          </a>
          {busyStatus}
        </>
      );
    }

    return (
      <>
        <ShadcnButton
          // Replaces the base `h-10`, so anchors (which have no base) keep `auto`.
          className={cn(nativeButtonHeightClass(variant, size), classNames)}
          ref={ref as React.Ref<HTMLButtonElement>}
          asChild={clampTrigger ? false : asChild}
          data-hen-id={COMPONENT_IDS.Button}
          {...props}
          type={clampTrigger ? 'button' : props.type}
          aria-busy={isBusy ? true : props['aria-busy']}
          aria-disabled={isBusy ? true : props['aria-disabled']}
          onClick={(event) => {
            if (isBusy) {
              event.preventDefault();
              return;
            }
            onClick?.(event);
            if (clampTrigger) {
              event.preventDefault();
              clampTrigger.onToggle();
            }
          }}
          aria-expanded={clampTrigger?.expanded ?? props['aria-expanded']}
          aria-controls={clampTrigger?.contentId ?? props['aria-controls']}
        >
          {clampTrigger ? content : asChild ? children : content}
        </ShadcnButton>
        {busyStatus}
      </>
    );
  },
);

Button.displayName = 'Button';
